import { supabase } from '../supabase';
import type { ClientContact } from '../types';

// Local-part generiche: NON deduplicano per email (creano sempre persona nuova),
// allineato a sync_contact_to_person (backfill).
const GENERIC_LOCALPARTS = new Set([
  'info', 'amministrazione', 'noreply', 'no-reply', 'pec', 'ordini',
  'commerciale', 'segreteria', 'contatti', 'vendite', 'sales', 'support', 'assistenza',
]);

function isGenericEmail(email?: string | null): boolean {
  const e = (email || '').trim().toLowerCase();
  if (!e || !e.includes('@')) return false;
  return GENERIC_LOCALPARTS.has(e.split('@')[0]);
}

/**
 * Persiste i "contatti" di un'azienda-cliente come PERSONE reali clients(individual)
 * + link client_relationships('contatto'). Sostituisce la scrittura legacy su client_contacts.
 *
 * Per ogni contatto:
 *  - se ha email non-generica e combacia con una persona (individual) esistente → riusa (no duplicato)
 *  - altrimenti crea una persona nuova con invarianti referente (stato/pipeline/newsletter_email NULL),
 *    ereditando però il consenso newsletter dal contatto
 *  - crea/aggiorna (idempotente) il link persona→azienda con gli attributi di relazione
 *
 * Ritorna gli id delle PERSONE (clients individual) risolte/create, in ordine dei contatti
 * effettivamente persistiti (saltati quelli senza né name né email). Serve ai chiamanti che
 * devono poi collegare la persona altrove (es. deal_contacts.person_client_id).
 */
export async function persistClientReferenti(
  companyClientId: string,
  orgId: string,
  contacts: ClientContact[],
): Promise<string[]> {
  const personIds: string[] = [];
  // Attribuzione creatore per il widget "Movimentazione lead del team"
  const { data: authData } = await supabase.auth.getUser();
  const currentUserId = authData?.user?.id ?? null;
  for (const c of contacts) {
    if (!c?.name && !c?.email) continue;
    const email = (c.email || '').trim() || null;
    let personId: string | null = null;

    // Contatto già persistito (id = client_relationships.id, non "temp_*" del form):
    // risolvi la persona dalla relazione esistente, per preservarne l'identità anche
    // quando l'email viene aggiunta/modificata in questa stessa modifica (senza questo,
    // il match per email sotto non trova nulla e crea una persona duplicata).
    let legacyContactId: string | null = null;
    if (c.id && !c.id.startsWith('temp_')) {
      const { data: existingLink } = await supabase
        .from('client_relationships')
        .select('source_client_id')
        .eq('id', c.id)
        .eq('organization_id', orgId)
        .eq('target_client_id', companyClientId)
        .eq('relationship_type', 'contatto')
        .maybeSingle();
      personId = existingLink?.source_client_id ?? null;

      // Id reale ma NON è una relazione: è un contatto legacy client_contacts
      // (arriva dal merge legacy+referenti di fetchClientById). Senza questa
      // risoluzione, un contatto legacy senza email creava una persona NUOVA
      // a OGNI risalvataggio del cliente (visti 49 duplicati in prod).
      if (!personId) {
        const { data: legacyRow } = await supabase
          .from('client_contacts')
          .select('id, person_client_id')
          .eq('id', c.id)
          .eq('organization_id', orgId)
          .maybeSingle();
        if (legacyRow) {
          legacyContactId = legacyRow.id;
          personId = legacyRow.person_client_id ?? null;
        }
      }
    }

    // Dedup (solo contatti nuovi): persona individual esistente con stessa email
    // (salvo email generiche), per riusare una persona già contatto altrove
    if (!personId && email && !isGenericEmail(email)) {
      const { data: existing } = await supabase
        .from('clients')
        .select('id')
        .eq('organization_id', orgId)
        .eq('entity_type', 'individual')
        .ilike('email', email)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      personId = existing?.id ?? null;
    }

    // Dedup per telefono (contatti SENZA email: unico identificativo disponibile).
    // Senza questo, i flussi che passano solo name+phone (WhatsApp, legacy senza
    // email) creano una persona nuova a ogni chiamata.
    const phone = (c.phone || '').trim() || null;
    if (!personId && !email && phone) {
      const { data: existingByPhone } = await supabase
        .from('clients')
        .select('id')
        .eq('organization_id', orgId)
        .eq('entity_type', 'individual')
        .eq('phone', phone)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();
      personId = existingByPhone?.id ?? null;
    }

    // Se la persona ESISTE già (match per email), aggiorna comunque i campi anagrafici
    // editati nel form. Senza questo, modificare telefono/nome/data-nascita di un contatto
    // esistente non salverebbe nulla sulla persona (si aggiornerebbe solo la relazione sotto).
    // Aggiorna solo i campi valorizzati nel form per non azzerare dati esistenti.
    if (personId) {
      const personPatch: Record<string, unknown> = {};
      if (c.name) personPatch.business_name = c.name;
      if (email) personPatch.email = email;
      if (c.phone) personPatch.phone = c.phone;
      if (c.birth_date) personPatch.birth_date = c.birth_date;
      // Il toggle newsletter del form deve aggiornare anche le persone esistenti
      // (prima veniva scritto solo alla creazione). Guard !== undefined perché
      // molti call-site passano contatti parziali senza questo campo.
      if (c.newsletter_subscribed !== undefined) personPatch.newsletter_subscribed = c.newsletter_subscribed;
      if (Object.keys(personPatch).length > 0) {
        const { error: updErr } = await supabase
          .from('clients')
          .update(personPatch)
          .eq('id', personId)
          .eq('organization_id', orgId)
          .eq('entity_type', 'individual');
        if (updErr) console.error('[persistClientReferenti] update person error:', updErr);
      }
    }

    // Altrimenti crea la persona (invarianti referente + consenso ereditato)
    if (!personId) {
      const { data: created, error: createErr } = await supabase
        .from('clients')
        .insert({
          organization_id: orgId,
          business_name: c.name || email,
          entity_type: 'individual',
          email,
          phone: c.phone || null,
          birth_date: c.birth_date || null,
          stato_cliente: null,
          pipeline_id: null,
          newsletter_email: null,
          newsletter_subscribed: c.newsletter_subscribed || false,
          created_by: currentUserId,
        })
        .select('id')
        .single();
      if (createErr) {
        console.error('[persistClientReferenti] create person error:', createErr);
        continue;
      }
      personId = created?.id ?? null;
    }
    if (!personId) continue;

    // Tag della persona dal form referente. Solo se il form li ha caricati
    // (undefined = non toccare: import, WhatsApp e gli altri contatti della lista).
    // Diff diretto, senza gli eventi workflow "tag cliente" di assignTagsToClient.
    if (c.tag_ids !== undefined) {
      const { data: currentTags } = await supabase
        .from('client_tag_assignments')
        .select('tag_id')
        .eq('client_id', personId);
      const current = (currentTags || []).map((t: { tag_id: string }) => t.tag_id);
      const toRemove = current.filter((id) => !c.tag_ids!.includes(id));
      const toAdd = c.tag_ids.filter((id) => !current.includes(id));
      if (toRemove.length > 0) {
        const { error: delErr } = await supabase
          .from('client_tag_assignments')
          .delete()
          .eq('client_id', personId)
          .in('tag_id', toRemove);
        if (delErr) console.error('[persistClientReferenti] remove tags error:', delErr);
      }
      if (toAdd.length > 0) {
        const { error: addErr } = await supabase
          .from('client_tag_assignments')
          .insert(toAdd.map((tagId) => ({
            organization_id: orgId, client_id: personId, tag_id: tagId, assigned_by: currentUserId,
          })));
        if (addErr) console.error('[persistClientReferenti] add tags error:', addErr);
      }
    }

    // Write-back sul contatto legacy: mappa la persona risolta/creata così i
    // salvataggi futuri la riusano (idempotenza) e il merge UI può deduplicare.
    if (legacyContactId) {
      const { error: mapErr } = await supabase
        .from('client_contacts')
        .update({ person_client_id: personId })
        .eq('id', legacyContactId)
        .eq('organization_id', orgId);
      if (mapErr) console.error('[persistClientReferenti] legacy map error:', mapErr);
    }

    // Link idempotente persona → azienda con attributi di relazione.
    // Merge col link esistente: i call-site parziali (import CSV/OneDrive,
    // WhatsApp) non devono azzerare role/area/notes/flag già valorizzati —
    // un campo undefined significa "non toccare", non "svuota".
    const { data: existingRel } = await supabase
      .from('client_relationships')
      .select('role, area, notes, is_referente, is_rappresentante_legale, is_primary_contact')
      .eq('source_client_id', personId)
      .eq('target_client_id', companyClientId)
      .eq('relationship_type', 'contatto')
      .maybeSingle();
    const keep = <T,>(incoming: T | undefined, current: T | null | undefined, fallback: T): T =>
      incoming !== undefined ? incoming : (current ?? fallback);
    const { error: linkErr } = await supabase
      .from('client_relationships')
      .upsert({
        source_client_id: personId,
        target_client_id: companyClientId,
        relationship_type: 'contatto',
        organization_id: orgId,
        role: c.role !== undefined ? (c.role || null) : (existingRel?.role ?? null),
        area: c.area !== undefined ? (c.area || null) : (existingRel?.area ?? null),
        notes: (c as any).notes !== undefined ? ((c as any).notes || null) : (existingRel?.notes ?? null),
        is_referente: keep(c.is_referente, existingRel?.is_referente, false),
        is_rappresentante_legale: keep(c.is_rappresentante_legale, existingRel?.is_rappresentante_legale, false),
        is_primary_contact: keep(c.is_primary, existingRel?.is_primary_contact, false),
        is_active: true,
      }, { onConflict: 'source_client_id,target_client_id,relationship_type' });
    if (linkErr) console.error('[persistClientReferenti] link error:', linkErr);
    else personIds.push(personId);
  }
  return personIds;
}
